// /app/frontend/src/pages/auth/MFASetupPage.jsx
// MFA setup page — shown when login requires enforced MFA setup
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Shield, Copy, Loader2, ArrowLeft, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

export default function MFASetupPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithToken } = useAuth();

  const enforced = location.state?.enforced || false;
  const message = location.state?.message || '';

  const [step, setStep] = useState('start'); // start | scan | backup
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [backupCodes, setBackupCodes] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('mfa_setup_token');
    if (!token) {
      navigate('/login', { replace: true });
    }
  }, [navigate]);

  const startSetup = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('mfa_setup_token');
      const res = await api.post('/mfa/setup', {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSetupData(res.data);
      setStep('scan');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to initiate MFA setup');
    } finally {
      setLoading(false);
    }
  };

  const confirmSetup = async () => {
    if (verifyCode.length !== 6) return;
    setLoading(true);
    try {
      const token = localStorage.getItem('mfa_setup_token');
      const res = await api.post('/mfa/verify-setup',
        { code: verifyCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setBackupCodes(res.data.backup_codes);
      setStep('backup');
      toast.success('MFA enabled successfully!');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Invalid code. Please try again.');
      setVerifyCode('');
    } finally {
      setLoading(false);
    }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(setupData?.secret || '');
    toast.success('Secret key copied');
  };

  const copyBackupCodes = () => {
    navigator.clipboard.writeText(backupCodes.join('\n'));
    toast.success('Backup codes copied');
  };

  const completeSetup = async () => {
    // Exchange mfa_setup_token for full access token
    try {
      setLoading(true);
      const mfaToken = localStorage.getItem('mfa_setup_token');
      localStorage.removeItem('mfa_setup_token');

      // Since MFA setup is complete, now exchange for full access
      // Backend will provide full token after verify-setup succeeds
      // For enforced setup, just redirect to dashboard
      navigate('/dashboard', { replace: true });
    } catch (e) {
      toast.error('Failed to complete setup');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 flex items-center justify-center px-4">
      <div className="w-full max-w-sm space-y-6">
        {/* Enforced MFA Banner */}
        {enforced && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4 flex gap-3">
            <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
                {message || 'MFA Setup Required'}
              </p>
              <p className="text-xs text-amber-800 dark:text-amber-300">
                Your organization requires two-factor authentication. Please set it up to continue.
              </p>
            </div>
          </div>
        )}

        <div className="text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-violet-600/20 border border-violet-500/30 mb-4">
            <Shield className="h-7 w-7 text-violet-400" />
          </div>
          <h1 className="text-2xl font-bold text-white">Set Up Two-Factor Authentication</h1>
          <p className="text-slate-400 text-sm mt-1">
            Secure your account with an authenticator app
          </p>
        </div>

        {step === 'start' && (
          <div className="space-y-4">
            <div className="bg-slate-900 rounded-lg p-4 space-y-2">
              <p className="text-sm text-slate-300">
                Two-factor authentication adds an extra layer of security to your account. You'll need to enter a code from your authenticator app when you log in.
              </p>
            </div>
            <Button
              className="w-full bg-violet-600 hover:bg-violet-700"
              size="lg"
              onClick={startSetup}
              disabled={loading}
            >
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Shield className="h-4 w-4 mr-2" />}
              Begin Setup
            </Button>
          </div>
        )}

        {step === 'scan' && (
          <div className="space-y-5">
            <p className="text-sm text-slate-300 font-medium">Step 1: Scan this QR code with your authenticator app:</p>
            <div className="text-sm text-slate-400 space-y-2">
              <p>Download one of these apps if you don't have one:</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>Google Authenticator</li>
                <li>Microsoft Authenticator</li>
                <li>Authy</li>
                <li>1Password</li>
              </ul>
            </div>
            {setupData?.qr_url && (
              <div className="flex flex-col items-center gap-3">
                <div className="p-3 bg-white rounded-xl">
                  <img src={setupData.qr_url} alt="QR Code" className="w-48 h-48" />
                </div>
                <div className="w-full">
                  <Label className="text-xs text-slate-400">Can't scan? Enter this key manually:</Label>
                  <div className="flex items-center gap-2 mt-1.5">
                    <code className="flex-1 px-3 py-2 bg-slate-900 rounded-lg text-xs font-mono text-slate-300 border border-slate-700 break-all">
                      {setupData.secret}
                    </code>
                    <Button variant="ghost" size="icon" className="shrink-0 h-9 w-9" onClick={copySecret}>
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-slate-200">Step 2: Enter the 6-digit code from your app</Label>
              <Input
                value={verifyCode}
                onChange={e => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                className="bg-slate-900 border-slate-700 text-center text-2xl tracking-[0.4em] h-12"
                inputMode="numeric"
                autoComplete="one-time-code"
              />
            </div>
            <Button
              className="w-full bg-violet-600 hover:bg-violet-700"
              size="lg"
              onClick={confirmSetup}
              disabled={verifyCode.length !== 6 || loading}
            >
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Verify & Continue
            </Button>
          </div>
        )}

        {step === 'backup' && (
          <div className="space-y-4">
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 space-y-2">
              <p className="text-sm font-medium text-amber-900 dark:text-amber-200">Save your backup codes</p>
              <p className="text-xs text-amber-800 dark:text-amber-300">
                Keep these codes in a safe place. Each code can only be used once if you lose access to your authenticator app.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {backupCodes.map((c, i) => (
                <code key={i} className="px-3 py-2 bg-slate-900 rounded-lg text-sm font-mono text-center text-slate-200 border border-slate-700">{c}</code>
              ))}
            </div>
            <Button variant="outline" size="sm" onClick={copyBackupCodes} className="w-full">
              <Copy className="h-3.5 w-3.5 mr-2" /> Copy All Backup Codes
            </Button>
            <Button
              className="w-full bg-violet-600 hover:bg-violet-700"
              size="lg"
              onClick={completeSetup}
              disabled={loading}
            >
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Continue to Dashboard
            </Button>
            <p className="text-xs text-emerald-400 text-center">MFA is now active on your account.</p>
          </div>
        )}

        {!enforced && (
          <button
            type="button"
            onClick={() => navigate('/login')}
            className="w-full text-sm text-slate-500 hover:text-slate-400 flex items-center justify-center gap-1 py-2"
          >
            <ArrowLeft className="h-3 w-3" /> Back to login
          </button>
        )}
      </div>
    </div>
  );
}
